/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {mat4, vec3} from "gl-matrix";

/**
 * @param {vec3} [aMin]
 * @param {vec3} [aMax]
 * @constructor
 */

export default
class BoundingBox {

    private min: vec3 = vec3.create();
    private max: vec3 = vec3.create();
    private radialVec: vec3 = vec3.create();
    private center: vec3 = vec3.create();
    private diameter: number = 0;

    private _tmpVec3: vec3 = vec3.create();
    private _tmpVec3B: vec3 = vec3.create();

    constructor(aMin?: vec3, aMax?: vec3)
    {
        this.setMinMax(aMin, aMax);
    }

    /**
     * Reset all values
     */
    public init(): void {
        for (let i = 0; i < 3; ++i) {
            this.min[i] = 9007199254740992;
            this.max[i] = -9007199254740992;
        }

        vec3.set(this.radialVec, 0, 0, 0);
        vec3.set(this.center, 0, 0, 0);
        this.diameter = 0;
    }

    /**
     * @return {vec3}
     */
    public getMin(): vec3 {
        vec3.copy(this._tmpVec3, this.min);
        return this._tmpVec3;
    }

    /**
     * @return {vec3}
     */
    public getMax(): vec3 {
        vec3.copy(this._tmpVec3, this.max);
        return this._tmpVec3;
    }

    /**
     * @return {vec3}
     */
    public getCenter(): vec3 {
        vec3.copy(this._tmpVec3, this.center);
        return this._tmpVec3;
    }

    /**
     * @return {number}
     */
    public getDiameter(): number {
        return this.diameter;
    }

    public getOptimalViewVector(): vec3
    {
        let dx = Math.abs(this.max[0] - this.min[0]);
        let dy = Math.abs(this.max[1] - this.min[1]);
        let dz = Math.abs(this.max[2] - this.min[2]);

        let pxy = dx * dy;
        let pyz = dy * dz;
        let pxz = dx * dz;

        let vec = vec3.create();
        vec3.normalize(vec, vec3.fromValues(pyz, pxz, pxy));

        // prevent look direct up or down
        if (Math.abs(vec[1] - 1.0) < 0.001) {
            vec[1] = vec[1] < 0.0 ? -0.999 : 0.999;
        }
        return vec;
    }

    /**
     * @return {number}
     */
    public getRadius(): number {
        return this.diameter / 2.0;
    }

    /**
     * @param {cadwork.webgl.utils.BoundingBox} aBB
     */
    public copyFrom(aBB: BoundingBox): void {
        vec3.copy(this.min, aBB.min);
        vec3.copy(this.max, aBB.max);
        vec3.copy(this.radialVec, aBB.radialVec);
        vec3.copy(this.center, aBB.center);
        this.diameter = aBB.diameter;
    }

    /**
     * @param {vec3} [aMin]
     * @param {vec3} [aMax]
     */
    public setMinMax(aMin: vec3, aMax: vec3): void {
        if (aMin === undefined || aMax === undefined) {
            this.init();
        }
        else {
            vec3.copy(this.min, aMin);
            vec3.copy(this.max, aMax);
            this.updateInternals();
        }
    }

    public getDimensions(): vec3
    {
        return vec3.fromValues(this.max[0] - this.min[0], this.max[1] - this.min[1], this.max[2] - this.min[2]);
    }

    /**
     * @return {cadwork.webgl.utils.BoundingBox}
     */
    public clone(): BoundingBox {
        return new BoundingBox(this.min, this.max);
    }

    private updateInternals(): void
    {
        vec3.subtract(this.radialVec, this.max, this.min);
        vec3.scaleAndAdd(this.center, this.min, this.radialVec, 0.5);

        this.diameter  = 2 * vec3.length(this.radialVec);
    }

    public transformMat4(aTransformation: mat4| Float32Array): boolean {
        if (!this.isValid()) {
            return false;
        }

        // we need to transform at least four points (when we transform only min, max then when rotation e.g. around y axis, at 45 degrees they will have same y value so the bbox will be flat)

        // tmpVec3 ... Min
        // tmpVec3B .. Max
        vec3.transformMat4(this._tmpVec3, this.min, aTransformation);
        vec3.transformMat4(this._tmpVec3B, this.max, aTransformation);

        let tmpVec3C = vec3.create();
        let tmpVec3D = vec3.create();
        vec3.transformMat4(tmpVec3C, vec3.fromValues(this.max[0], this.min[1], this.min[2]), aTransformation);
        vec3.transformMat4(tmpVec3D, vec3.fromValues(this.min[0], this.max[1], this.max[2]), aTransformation);

        // reinit the bbox
        this.init();
        this.extendByPoint(this._tmpVec3);
        this.extendByPoint(this._tmpVec3B);
        this.extendByPoint(tmpVec3C);
        this.extendByPoint(tmpVec3D);
        this.updateInternals();

        return true;
    }

    /**
     * @param {vec3} aMin
     * @param {vec3} aMax
     */
    public setBounds(aMin: vec3, aMax: vec3): void
    {
        vec3.copy(this.min, aMin);
        vec3.copy(this.max, aMax);
        this.updateInternals();
    }

    /**
     * @return {boolean}
     */
    public hasVolume(): boolean
    {
        return !vec3.equals(this.min, this.max);
    }

    /**
     * @param {vec3} aPoint
     */
    public extendByPoint(aPoint: vec3 | number[]): void {
        if (!aPoint) {
            return;
        }

        if (this.min[0] > aPoint[0]) { this.min[0] = aPoint[0]; }
        if (this.min[1] > aPoint[1]) { this.min[1] = aPoint[1]; }
        if (this.min[2] > aPoint[2]) { this.min[2] = aPoint[2]; }

        if (this.max[0] < aPoint[0]) { this.max[0] = aPoint[0]; }
        if (this.max[1] < aPoint[1]) { this.max[1] = aPoint[1]; }
        if (this.max[2] < aPoint[2]) { this.max[2] = aPoint[2]; }

        this.updateInternals();
    }

    /**
     * @param {number} aX
     * @param {number} aY
     * @param {number} aZ
     */
    public extendByXYZ(aX: number, aY: number, aZ: number): void {
        if (this.min[0] > aX) { this.min[0] = aX; }
        if (this.min[1] > aY) { this.min[1] = aY; }
        if (this.min[2] > aZ) { this.min[2] = aZ; }

        if (this.max[0] < aX) { this.max[0] = aX; }
        if (this.max[1] < aY) { this.max[1] = aY; }
        if (this.max[2] < aZ) { this.max[2] = aZ; }

        this.updateInternals();
    };

    /**
     * @param {cadwork.webgl.utils.BoundingBox} aBBox
     */
    public extendByBBox(aBBox: BoundingBox): void {
        if (!aBBox || !aBBox.isValid()) {
            return;
        }

        if (this.min[0] > aBBox.min[0]) { this.min[0] = aBBox.min[0]; }
        if (this.min[1] > aBBox.min[1]) { this.min[1] = aBBox.min[1]; }
        if (this.min[2] > aBBox.min[2]) { this.min[2] = aBBox.min[2]; }

        if (this.max[0] < aBBox.max[0]) { this.max[0] = aBBox.max[0]; }
        if (this.max[1] < aBBox.max[1]) { this.max[1] = aBBox.max[1]; }
        if (this.max[2] < aBBox.max[2]) { this.max[2] = aBBox.max[2]; }

        this.updateInternals();
    }

    /**
     * @return {boolean}
     */
    public isValid(): boolean {
        return (this.min[0] <= this.max[0]) && (this.min[1] <= this.max[1]) && (this.min[2] <= this.max[2]);
    }

    /**
     * @param {vec3} aPoint
     * @return {boolean}
     */
    public containsPoint(aPoint: vec3): boolean {
        return (this.min[0] <= aPoint[0]) && (this.min[1] <= aPoint[1]) && (this.min[2] <= aPoint[2]) &&
            (this.max[0] >= aPoint[0]) && (this.max[1] >= aPoint[1]) && (this.max[2] >=  aPoint[2]);
    }

    /**
     * @param {vec3} aPoint
     * @return {vec3}
     */
    public getClosestPoint(aPoint: vec3): vec3 {
        vec3.copy(this._tmpVec3, aPoint);

        let dx = this._tmpVec3[0] - this.center[0];
        let dy = this._tmpVec3[1] - this.center[1];
        let dz = this._tmpVec3[2] - this.center[2];

        // Move point to be on the nearest plane of the box.
        if ((Math.abs(dx) > Math.abs(dy)) && (Math.abs(dx) > Math.abs(dz))) {
            this._tmpVec3[0] = this.center[0] + this.radialVec[0] * ((dx < 0.0) ? -1.0 : 1.0);
            //this._tmpVec3[0] = Math.min(Math.max(this._tmpVec3[0], this.min[0]), this.max[0]);
        }
        else if (Math.abs(dy) > Math.abs(dz)) {
            this._tmpVec3[1] = this.center[1] + this.radialVec[1] * ((dy < 0.0) ? -1.0 : 1.0);
            //this._tmpVec3[1] = Math.min(Math.max(this._tmpVec3[1], this.min[1]), this.max[1]);
        }
        else {
            this._tmpVec3[2] = this.center[2] + this.radialVec[2] * ((dz < 0.0) ? -1.0 : 1.0);
            //this._tmpVec3[2] = Math.min(Math.max(this._tmpVec3[2], this.min[2]), this.max[2]);
        }

        // Clamp to be inside box.
        this._tmpVec3[0] = Math.min(Math.max(this._tmpVec3[0], this.min[0]), this.max[0]);
        this._tmpVec3[1] = Math.min(Math.max(this._tmpVec3[1], this.min[1]), this.max[1]);
        this._tmpVec3[2] = Math.min(Math.max(this._tmpVec3[2], this.min[2]), this.max[2]);

        return this._tmpVec3;
    }

    public intersection(out: vec3, ro: vec3, rd: vec3): vec3 {
        let d = this.distance(ro, rd);
        if (d === Infinity) {
            out = null;
        } else {
            out = out || vec3.create();
            for (let i = 0; i < ro.length; i++) {
                out[i] = ro[i] + rd[i] * d
            }
        }

        return out;
    }

    public distance(ro: vec3, rd: vec3): number {
        let dims = 3;
        let lo = -Infinity;
        let hi = +Infinity;

        for (let i = 0; i < dims; i++) {
            let dimLo = (this.min[i] - ro[i]) / rd[i];
            let dimHi = (this.max[i] - ro[i]) / rd[i];

            if (dimLo > dimHi) {
                let tmp = dimLo;
                dimLo = dimHi;
                dimHi = tmp;
            }

            if (dimHi < lo || dimLo > hi) {
                return Infinity;
            }

            if (dimLo > lo) lo = dimLo;
            if (dimHi < hi) hi = dimHi;
        }

        return lo > hi ? Infinity : lo;
    }
}
